import { useState, useEffect } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { NumberInput, Select, ActionIcon, Tooltip, Group, Text } from '@mantine/core';
import { IconTrash, IconEdit, IconCheck, IconX } from '@tabler/icons-react';
import type { IConfiguracionVariacionMensual } from '../interfaces';

export interface VariacionMensualTableMeta {
  actualizarFila: (id: string, campo: keyof IConfiguracionVariacionMensual, valor: string | number) => void;
  eliminarFila: (id: string) => void;
  guardarFila?: (id: string) => void;
  filaEnEdicion?: string | null;
  iniciarEdicion?: (fila: any) => void;
  cancelarEdicion?: () => void;
  mostrarNotificacionObligatorio?: () => void;
  intentoGuardar?: boolean;
}

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const EditableSelectCell = ({ getValue, row, column, table }: any) => {
  const initialValue = getValue() as string;
  const [value, setValue] = useState<string | null>(initialValue || null);
  const [touched, setTouched] = useState(false);
  const meta = table.options.meta as VariacionMensualTableMeta | undefined;
  const campo = column.id as keyof IConfiguracionVariacionMensual;
  const rowId = row.original.Id;

  useEffect(() => { setValue(initialValue || null); }, [initialValue]);

  const isEditing = String(meta?.filaEnEdicion) === String(rowId);
  if (!isEditing) {
    return <Text size="xs">{value || "-"}</Text>;
  }
  const data = table.options.data as IConfiguracionVariacionMensual[];
  const mesesUsados = data.filter(d => String(d.Id) !== String(rowId)).map(d => d.Mes);
  const opcionesMeses = MESES.map(mes => ({
    value: mes,
    label: mes,
    disabled: mesesUsados.includes(mes)
  }));

  return (
    <Select
      size="xs"
      value={value}
      onChange={(val) => { setTouched(true); setValue(val); if (meta) meta.actualizarFila(rowId, campo, val || ""); }}
      data={opcionesMeses}
      placeholder="Mes *"
      required
      withAsterisk
      error={(touched || meta?.intentoGuardar) && (!value || String(value).trim() === "")}
      searchable
      nothingFoundMessage="Sin resultados"
      allowDeselect={false}
      comboboxProps={{ withinPortal: true, zIndex: 9999 }}
      styles={{ input: { fontSize: "12px", padding: "4px 8px" } }}
    />
  );
};

const EditableNumberCell = ({ getValue, row, column, table }: any) => {
  const initialValue = getValue() as number;
  const [value, setValue] = useState<number | string>(initialValue);
  const [touched, setTouched] = useState(false);
  const meta = table.options.meta as VariacionMensualTableMeta | undefined;
  const campo = column.id as keyof IConfiguracionVariacionMensual;
  const rowId = row.original.Id;

  useEffect(() => { setValue(initialValue); }, [initialValue]);

  const isEditing = String(meta?.filaEnEdicion) === String(rowId);
  if (!isEditing) {
    return <Text size="xs">{Number(value) > 0 ? '+' : ''}{value} %</Text>;
  }
  return (
    <NumberInput
      size="xs"
      hideControls
      value={value !== 0 ? value : (value === 0 ? "0" : "")}
      onChange={(val) => { setTouched(true); setValue(val); if (meta) meta.actualizarFila(rowId, campo, val === "" ? "" : (Number(val) || 0)); }}
      // Positivo si el flete sube en el mes, negativo si baja (más de -100 %).
      min={-99.99}
      placeholder="Variación *"
      required
      withAsterisk
      error={(touched || meta?.intentoGuardar) && (value === null || value === undefined || String(value) === "")}
      styles={{ input: { fontSize: "12px", padding: "4px 8px" } }}
      suffix=" %"
      decimalScale={2}
    />
  );
};

export const obtenerColumnasVariacionMensual = (): ColumnDef<IConfiguracionVariacionMensual>[] => [
  {
    id: "acciones",
    header: "Acciones",
    size: 90,
    cell: ({ row, table }) => {
      const meta = table.options.meta as VariacionMensualTableMeta | undefined;
      const isEditing = String(meta?.filaEnEdicion) === String(row.original.Id);
      const hayOtraFilaEnEdicion = meta?.filaEnEdicion !== null && meta?.filaEnEdicion !== undefined && String(meta?.filaEnEdicion) !== String(row.original.Id);
      
      if (isEditing) {
        return (
          <Group gap={4} justify="center">
            <Tooltip label="Guardar" withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
              <ActionIcon variant="subtle" color="var(--mantine-color-primary-0)" size="sm" radius="xl" onClick={() => {
                const filaOriginal = row.original;
                if (!filaOriginal.Mes || String(filaOriginal.Mes).trim() === '' || filaOriginal.Porcentaje === null || filaOriginal.Porcentaje === undefined || String(filaOriginal.Porcentaje) === '') {
                  meta?.mostrarNotificacionObligatorio?.();
                  return;
                }
                meta?.guardarFila?.(filaOriginal.Id);
              }}>
                <IconCheck size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Cancelar" withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
              <ActionIcon variant="subtle" color="#df675a" size="sm" radius="xl" onClick={() => meta?.cancelarEdicion?.()}>
                <IconX size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        );
      }
      return (
        <Group gap={4} justify="center">
          <Tooltip label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Editar"} withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
             <ActionIcon disabled={hayOtraFilaEnEdicion} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta?.iniciarEdicion?.(row.original)}>
               <IconEdit size={18} />
             </ActionIcon>
          </Tooltip>
          <Tooltip label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Eliminar"} withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
            <ActionIcon disabled={hayOtraFilaEnEdicion} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta?.eliminarFila(row.original.Id)}>
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      );
    }
  },
  {
    accessorKey: "Mes",
    header: "Mes",
    cell: EditableSelectCell,
    size: 200,
  },
  {
    accessorKey: "Porcentaje",
    header: "Variación",
    cell: EditableNumberCell,
    size: 150,
  }
];
